/* Marquee select on the forma paper — drag a box to select every token it
   touches; a click that never moved is an empty-paper click (the host
   clears its selection). Ported from the classic canvas's beginMarquee and
   shared by Forma Studio and the project editor.

   Explorer semantics: a plain rubber-band REPLACES the selection,
   Ctrl/Cmd adds to it. Shift belongs to click-ranging (DESIGN §3.4). */

import React from 'react';
import { createPortal } from 'react-dom';
import { CanvasSelectionContext } from './TokenComponents';
import { shouldClearOnEmptyClick } from './useFormaSelection';
import './paper/paper.css';

export interface TokenMarqueeOpts {
  /** An unmodified click on empty paper (no drag) — clear the selection. */
  onEmptyClick: () => void;
  /** A selector a press must NOT start inside (e.g. the document's text
   *  editor, where a drag is a text selection). */
  exclude?: string;
}

export function useTokenMarquee(opts: TokenMarqueeOpts) {
  const canvasSel = React.useContext(CanvasSelectionContext);
  const optsRef = React.useRef(opts);
  optsRef.current = opts;
  const [marquee, setMarquee] = React.useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);

  const beginMarquee = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const tgt = e.target as HTMLElement;
      if (tgt.closest('[data-token-key]')) return; // a token handles its own drag
      if (tgt.closest('input,textarea,select,button,[role="switch"],[role="menu"]')) return;
      const exclude = optsRef.current.exclude;
      if (exclude && tgt.closest(exclude)) return;
      const additive = e.ctrlKey || e.metaKey;
      const x0 = e.clientX;
      const y0 = e.clientY;
      let moved = false;
      const onMove = (ev: MouseEvent) => {
        if (!moved && (Math.abs(ev.clientX - x0) > 4 || Math.abs(ev.clientY - y0) > 4))
          moved = true;
        if (moved) setMarquee({ x0, y0, x1: ev.clientX, y1: ev.clientY });
      };
      const onUp = (ev: MouseEvent) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        setMarquee(null);
        if (!moved) {
          // Ctrl/Cmd/Shift-modified clicks belong to the level / EOS / token
          // click handler that follows — never clear under them.
          if (!shouldClearOnEmptyClick(ev)) return;
          // A plain click on empty paper clears EVERYTHING — never sticky.
          optsRef.current.onEmptyClick();
          return;
        }
        const rx0 = Math.min(x0, ev.clientX);
        const rx1 = Math.max(x0, ev.clientX);
        const ry0 = Math.min(y0, ev.clientY);
        const ry1 = Math.max(y0, ev.clientY);
        const hits: string[] = [];
        document.querySelectorAll('[data-token-key]').forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.left < rx1 && r.right > rx0 && r.top < ry1 && r.bottom > ry0) {
            const k = el.getAttribute('data-token-key');
            if (k) hits.push(k);
          }
        });
        canvasSel.selectMany(hits, additive);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [canvasSel]
  );

  /* The rectangle is drawn from raw client coordinates, so it MUST live on
     <body>: inside the dock panel an ancestor transform turns position:fixed
     into "fixed relative to that ancestor" and the box lands offset from the
     real mouse. Hit-testing always uses true viewport rects. */
  const marqueeNode = marquee
    ? createPortal(
        <div
          className="sfs-marquee"
          aria-hidden="true"
          style={{
            left: Math.min(marquee.x0, marquee.x1),
            top: Math.min(marquee.y0, marquee.y1),
            width: Math.abs(marquee.x1 - marquee.x0),
            height: Math.abs(marquee.y1 - marquee.y0),
          }}
        />,
        document.body
      )
    : null;

  return { beginMarquee, marqueeNode };
}
